import { ui } from '@libs/domain';
import { useItemNames } from '@ps-smart-cp/ppfw-client';
import { type ChangeEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  checkSandboxExistence,
  FormSection,
  initOrganizationState,
  LoginContainer,
  LoginCredentials,
  LoginInputField,
  LoginLabelField,
  StyledLoginButton,
  signIn,
} from '../features/login';
import { useTryShell } from '../hooks';
import type { OrganizationState } from '../types';

export function SandboxLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tryRequest, error, setError } = useTryShell();

  const state = location.state as OrganizationState | null;
  const { organizationId, organizationName, ssoStatus } = initOrganizationState(state);
  const [credentials, setCredentials] = useState(LoginCredentials.init(organizationId));

  const titleNames = useItemNames(ui.titleNames);
  const labelNames = useItemNames(ui.labelNames);
  const buttonNames = useItemNames(ui.buttonNames);

  useEffect(() => {
    if (!organizationId || !organizationName || !ssoStatus) {
      navigate('/', { replace: true });
    }
  }, [organizationId, ssoStatus]);

  const onChange = (e: ChangeEvent<HTMLInputElement>) =>
    setCredentials({ ...credentials, [e.currentTarget.name]: e.currentTarget.value });

  // AUTH-333 エラーメッセージのローカライズ
  const onLogin = async () =>
    await tryRequest(async () => {
      if (credentials.username === '' || credentials.password === '') {
        setError(
          '【ログインエラー】指定された組織ID、ユーザーID、パスワードに一致するアカウントがありません。(EFW56001)',
        );
        return;
      }

      await checkSandboxExistence(organizationName);

      const res = await signIn(credentials, true);

      if (res.challengeName === 'NEW_PASSWORD_REQUIRED') {
        navigate('/user/change-password/first-time', {
          state: {
            region: res.region,
            challengeName: res.challengeName,
            session: res.session,
            organizationId: res.organizationId,
            username: res.username,
            isSandbox: res.isSandbox,
          },
        });
      } else {
        navigate(`/${res.region}/main-menu`);
      }
    });

  return (
    <LoginContainer title={titleNames('sandboxLogin')} error={error}>
      <FormSection>
        <LoginLabelField
          id="organization-id"
          head={`${labelNames('organizationId')}:`}
          body={organizationId}
        />
        <LoginInputField
          id="user-id"
          name="username"
          label={`${labelNames('userId')}*`}
          value={credentials.username}
          onChange={onChange}
          onEnter={onLogin}
        />
        <LoginInputField
          id="password"
          name="password"
          label={`${labelNames('password')}*`}
          value={credentials.password}
          onChange={onChange}
          onEnter={onLogin}
          type="password"
        />
      </FormSection>
      <FormSection>
        <StyledLoginButton id="login-btn" displayName={buttonNames('login')} onClick={onLogin} />
        <StyledLoginButton
          id="return-btn"
          displayName={buttonNames('return')}
          onClick={() =>
            navigate(`/login?id=${organizationId.trim()}`, {
              state: { organizationName, ssoStatus },
            })
          }
        />
      </FormSection>
    </LoginContainer>
  );
}
